/**
 * "+25 stars" — the little pill that says you just earned something. REC-57,
 * priority 2 (David, 24 Sep: "when your balance rises").
 *
 * ── IT WATCHES THE NUMBER, NOT THE ACTIONS ──────────────────────────────────
 *
 * Awards happen in database triggers (099) and the client is never told what
 * a write earned — a comment's award is a side effect of its INSERT. So rather
 * than teach every write path to report stars, this compares `balanceEarned`
 * each time `my_stars()` is read. Every read goes through `loadMyStars`, which
 * calls `noteBalance`, so there is exactly one place the comparison happens
 * and no second copy of the arithmetic. It covers what an action-based pill
 * never could: a recognition maturing out of its fifteen-minute hold (063),
 * which is nobody's action.
 *
 * ── ONLY A RISE, AND ONLY AFTER A BASELINE ──────────────────────────────────
 *
 * The first read in a session sets the baseline and says nothing — otherwise
 * every page load would pop "+1,065". A FALL (a removal taking stars back, a
 * block refunding) moves the baseline silently: the pill celebrates, it does
 * not audit. Spending never moves `balanceEarned` at all (it is `stars_spent`).
 *
 * ── NOTHING IS STORED ───────────────────────────────────────────────────────
 *
 * The baseline lives in this module and dies with the tab. No "last seen"
 * column, no localStorage — a per-person record of when somebody last looked
 * is the ACC-22 shape arriving by default. The cost is stated: stars that
 * arrive while the hub is closed are in the number and the feed, and do not
 * pop. `resetStarPulse()` runs on sign-out, or the next person to sign in on
 * the same tab would be shown the difference between two people's balances.
 */

type Listener = (gained: number) => void

let baseline: number | null = null
const listeners = new Set<Listener>()
let refresher: (() => Promise<unknown>) | null = null
let pending: ReturnType<typeof setTimeout> | null = null

/** Called by `loadMyStars` with every `balanceEarned` it reads. */
export function noteBalance(earned: number): void {
  if (!Number.isFinite(earned)) return
  const before = baseline
  baseline = earned
  if (before === null || earned <= before) return
  const gained = earned - before
  listeners.forEach((l) => l(gained))
}

export function onStarsEarned(l: Listener): () => void {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}

export function resetStarPulse(): void {
  baseline = null
}

/** stars.ts registers `loadMyStars` here, so this file imports nothing. */
export function setStarPulseRefresher(fn: () => Promise<unknown>): void {
  refresher = fn
}

/**
 * Take the baseline if nobody has yet. AppShell calls this on mount, because
 * a session that starts on #/wiki and comments there would otherwise spend its
 * first earn SETTING the baseline and pop nothing.
 */
export function primeStarPulse(): void {
  if (baseline === null && refresher) refresher().catch(() => {})
}

/**
 * "Something I just did may have earned stars — look." Called after a post,
 * a comment, a board question, an answer. Debounced, because a publish that
 * also posts a comment should cost one read, and it never throws: a pill that
 * does not appear is the whole failure mode, and it must not become an error
 * on the screen that did the real work.
 */
export function nudgeStars(delayMs = 400): void {
  if (!refresher) return
  if (pending) clearTimeout(pending)
  pending = setTimeout(() => {
    pending = null
    refresher?.().catch(() => {})
  }, delayMs)
}
